/**
 * Het oppervlak van de vragenlijst-schermen: de evaluaties (`questionnaire`) of de toetsen (`test`).
 *
 * Eén machine, twee oppervlakken (B38): dezelfde schermen en hooks bedienen allebei, en dit is
 * het enige dat ze van elkaar onderscheidt — de API-routes, de rechten, het adres in de app, de
 * soort van een nieuw formulier en de teksten. Een scherm leest zijn oppervlak uit de context die
 * de route eromheen zet; zonder provider is het `questionnaire`, dus bestaande schermen en
 * bestaande tests gedragen zich precies zoals voorheen.
 *
 * **Teksten.** `useSurfaceTranslation()` is een vervanger van `useTranslation()`. Op het
 * toetsoppervlak zoekt hij eerst `sleutel_test` en valt hij terug op `sleutel` (de contextfunctie
 * van i18next). Alleen wat anders moet klinken — *formulier* wordt *toets* — krijgt dus een
 * eigen sleutel; al het andere is gedeeld. Op het vragenlijstoppervlak geeft hij `t` ongewijzigd
 * terug: geen extra opties, geen andere uitkomst.
 *
 * **Query-sleutels** dragen het oppervlak (`questionnaire-forms` / `test-forms`), zodat een
 * toets en een evaluatie elkaar nooit uit de cache halen of stilzwijgend verversen.
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

export type Surface = 'questionnaire' | 'test';

export interface SurfaceConfig {
  surface: Surface;
  /** De route-segmenten van de API, zonder `/api/v1/`. */
  api: {
    questions: string;
    categories: string;
    forms: string;
    assignments: string;
    /** De toewijzingen van één training, en wat erbij hoort (`moment-preview`). */
    activityAssignments: (activityId: string) => string;
  };
  /** Waar de module in de app staat: de tabs hangen eronder. */
  basePath: string;
  /** Het deel van de query-sleutels: `questionnaire` geeft `questionnaire-forms`. */
  keyPrefix: string;
  permission: { view: string; manage: string; assign: string };
  /** De soort waarmee een nieuw formulier op dit oppervlak begint. */
  formKind: 'evaluation' | 'knowledge_test';
  /** Waar de toewijzingen van een training bewerkt worden (naast de lijst op `/activity/:id/questionnaires`). */
  activityEditorSegment: 'questionnaires' | 'tests';
}

export const SURFACES: Record<Surface, SurfaceConfig> = {
  questionnaire: {
    surface: 'questionnaire',
    api: {
      questions: '/api/v1/questionnaire-questions',
      categories: '/api/v1/questionnaire-categories',
      forms: '/api/v1/questionnaires',
      assignments: '/api/v1/questionnaire-assignments',
      activityAssignments: (activityId) => `/api/v1/learning-activities/${activityId}/questionnaire-assignments`,
    },
    basePath: '/questionnaires',
    keyPrefix: 'questionnaire',
    permission: { view: 'questionnaire.view', manage: 'questionnaire.manage', assign: 'questionnaire.assign' },
    formKind: 'evaluation',
    activityEditorSegment: 'questionnaires',
  },
  test: {
    surface: 'test',
    api: {
      questions: '/api/v1/test-questions',
      categories: '/api/v1/test-categories',
      forms: '/api/v1/tests',
      assignments: '/api/v1/test-assignments',
      activityAssignments: (activityId) => `/api/v1/learning-activities/${activityId}/test-assignments`,
    },
    basePath: '/tests',
    keyPrefix: 'test',
    permission: { view: 'test.view', manage: 'test.manage', assign: 'test.assign' },
    formKind: 'knowledge_test',
    activityEditorSegment: 'tests',
  },
};

const SurfaceContext = createContext<Surface>('questionnaire');

/** Zet het oppervlak voor alles eronder; de routes van de toetsen gebruiken `surface="test"`. */
export function SurfaceProvider({ surface, children }: { surface: Surface; children: ReactNode }) {
  return <SurfaceContext.Provider value={surface}>{children}</SurfaceContext.Provider>;
}

/** De configuratie van het oppervlak van dit scherm. Een scherm met twee oppervlakken zet een provider om elk deel. */
export function useSurface(): SurfaceConfig {
  return SURFACES[useContext(SurfaceContext)];
}

/**
 * `useTranslation()` met het oppervlak erin: op de toetsen eerst `sleutel_test`, dan `sleutel`.
 * Op het vragenlijstoppervlak is het resultaat hetzelfde object als dat van `useTranslation()`.
 */
export function useSurfaceTranslation() {
  const { surface } = useSurface();
  const translation = useTranslation();
  const { t } = translation;

  const surfaceT = useMemo(
    () => ((key: string, options?: Record<string, unknown>) => t(key, { ...options, context: 'test' })) as TFunction,
    [t],
  );

  return surface === 'test' ? { ...translation, t: surfaceT } : translation;
}
